import { Injectable } from '@angular/core';
import Quill from 'quill';
import ImageUploader from 'quill2-image-uploader';
import { ImageUploadService } from './image-upload.service';
import { EmojiApiService } from './emoji-api.service';

// Custom Emoji Module
class EmojiModule {
  private isPickerOpen = false;
  private quillInstance: any;
  private options: any;

  // caches - Fix the type here
  private categories: { slug: string; label: string }[] = [];
  private emojisByCategory = new Map<string, { character: string }[]>(); // Changed to array
  private loading = false;

  constructor(quill: any, options: any) {
    this.quillInstance = quill;
    this.options = options || {};
    const toolbar = quill.getModule('toolbar');
    if (toolbar) {
      toolbar.addHandler('emoji', () => this.toggleEmojiPicker(quill));
    }
  }

  async showEmojiPicker(quill: any) {
    const range = quill.getSelection();
    if (!range) return;

    // 1) si no tenemos categorías aún, pedirlas
    if (this.categories.length === 0 && !this.loading) {
      this.loading = true;
      try {
        const apiCats = await this.options.fetchCategories(); // [{slug, subCategories: []}]
        this.categories = (apiCats || [])
          .filter(
            (c: any) => c.slug && c.slug.trim() !== '' && c.slug !== 'component'
          ) // Filtrar categorías válidas y excluir 'component'
          .map((c: any) => ({
            slug: c.slug,
            label: this.pretty(c.slug), // Traducir al español
          }));

        // Si no hay categorías de la API, usar un fallback más completo
        if (this.categories.length === 0) {
          this.categories = [
            { slug: 'smileys-emotion', label: 'Emociones' },
            { slug: 'people-body', label: 'Personas' },
            { slug: 'animals-nature', label: 'Animales' },
            { slug: 'food-drink', label: 'Comida' },
            { slug: 'travel-places', label: 'Viajes' },
            { slug: 'activities', label: 'Actividades' },
            { slug: 'objects', label: 'Objetos' },
            { slug: 'symbols', label: 'Símbolos' },
            { slug: 'flags', label: 'Banderas' },
          ];
        }
      } catch (e) {
        console.error('Emoji categories error', e);
        // fallback completo para no romper UI
        this.categories = [
          { slug: 'smileys-emotion', label: 'Emociones' },
          { slug: 'people-body', label: 'Personas' },
          { slug: 'animals-nature', label: 'Animales' },
          { slug: 'food-drink', label: 'Comida' },
          { slug: 'travel-places', label: 'Viajes' },
          { slug: 'activities', label: 'Actividades' },
          { slug: 'objects', label: 'Objetos' },
          { slug: 'symbols', label: 'Símbolos' },
          { slug: 'flags', label: 'Banderas' },
        ];
      } finally {
        this.loading = false;
      }
    }

    // 2) crea el picker y pinta la primera categoría
    this.createEmojiPicker(quill, range, this.categories);
  }

  private pretty(slug: string) {
    // Traducir categorías al español
    const translations: { [key: string]: string } = {
      'smileys-emotion': 'Emociones',
      'people-body': 'Personas',
      'animals-nature': 'Animales',
      'food-drink': 'Comida',
      'travel-places': 'Viajes',
      activities: 'Actividades',
      objects: 'Objetos',
      symbols: 'Símbolos',
      flags: 'Banderas',
    };

    return (
      translations[slug] ||
      slug.split('-')[0].charAt(0).toUpperCase() + slug.split('-')[0].slice(1)
    );
  }

  private createEmojiPicker(
    quill: any,
    range: any,
    categories: { slug: string; label: string }[]
  ) {
    this.closeEmojiPicker();

    const picker = document.createElement('div');
    picker.className = 'custom-emoji-picker';
    picker.style.cssText = `
      position: absolute; z-index: 1000; background: white; border: 1px solid #ccc;
      border-radius: 8px; width: 800px; max-height: 400px; overflow: hidden;
      box-shadow: 0 4px 20px rgba(0,0,0,.15);
    `;

    const tabsContainer = document.createElement('div');
    tabsContainer.style.cssText = `display:flex;border-bottom:1px solid #eee;background:#f8f9fa;`;

    const contentContainer = document.createElement('div');
    contentContainer.style.cssText = `height:300px;overflow-y:auto;padding:10px;`;

    let activeSlug = categories[0]?.slug;

    // crea pestañas
    categories.forEach((cat, idx) => {
      const tab = document.createElement('button');
      tab.textContent = cat.label;
      tab.style.cssText = `
        flex:1;padding:8px 4px;border:none;background:${
          idx === 0 ? '#007bff' : 'transparent'
        };
        color:${
          idx === 0 ? 'white' : '#666'
        };cursor:pointer;font-size:12px;transition:all .2s;
      `;
      tab.addEventListener('click', (e) => {
        e.stopPropagation();
        tabsContainer.querySelectorAll('button').forEach((btn: any) => {
          btn.style.background = 'transparent';
          btn.style.color = '#666';
        });
        tab.style.background = '#007bff';
        tab.style.color = 'white';
        activeSlug = cat.slug;
        this.renderCategory(contentContainer, activeSlug, quill, range);
      });
      tabsContainer.appendChild(tab);
    });

    picker.appendChild(tabsContainer);
    picker.appendChild(contentContainer);

    // pinta la primera categoría
    this.renderCategory(contentContainer, activeSlug, quill, range);

    // posicionamiento
    const editorBounds = quill.getBounds(range.index);
    const containerRect = quill.container.getBoundingClientRect();
    picker.style.left =
      Math.min(
        containerRect.left + editorBounds.left,
        window.innerWidth - 470
      ) + 'px';
    picker.style.top = containerRect.top + editorBounds.bottom + 5 + 'px';

    document.body.appendChild(picker);
    this.isPickerOpen = true;

    // cerrar al hacer click fuera
    const closeHandler = (e: Event) => {
      const target = e.target as Element;
      const emojiButton = document.querySelector('.ql-emoji');
      if (!picker.contains(target) && !emojiButton?.contains(target)) {
        this.closeEmojiPicker();
        document.removeEventListener('click', closeHandler);
      }
    };
    setTimeout(() => document.addEventListener('click', closeHandler), 100);
  }

  private async renderCategory(
    container: HTMLElement,
    slug: string,
    quill: any,
    range: any
  ) {
    container.innerHTML =
      '<div style="padding:10px;font-size:12px;color:#666">Cargando…</div>';

    // cache / fetch
    if (!this.emojisByCategory.has(slug)) {
      try {
        const data = await this.options.fetchEmojisByCategory(slug); // EmojiItem[]
        const validEmojis = data
          .filter((e: any) => e.character && e.character.trim() !== '')
          .map((e: any) => ({ character: e.character }));

        this.emojisByCategory.set(slug, validEmojis); // Now correctly setting an array
      } catch (e) {
        console.error('Emoji fetch error for category:', slug, e);
        // Intentar obtener emojis de fallback para categorías populares
        const fallbackEmojis = this.getFallbackEmojis(slug);
        this.emojisByCategory.set(slug, fallbackEmojis);
      }
    }
    const emojis = this.emojisByCategory.get(slug)!; // This is now correctly an array

    // render
    container.innerHTML = '';
    container.style.cssText += `display:flex;flex-wrap:wrap;gap:3px;`;

    if (emojis.length === 0) {
      container.innerHTML =
        '<div style="padding:20px;font-size:14px;color:#666;text-align:center;">No hay emojis disponibles en esta categoría.</div>';
      return;
    }

    emojis.forEach(({ character }: { character: string }) => {
      // Fixed typing
      const btn = document.createElement('button');
      btn.textContent = character;
      btn.style.cssText = `
        border:none;background:none;font-size:20px;cursor:pointer;padding:5px;border-radius:4px;
        width:32px;height:32px;display:flex;align-items:center;justify-content:center;transition:background-color .2s;
      `;
      btn.addEventListener(
        'mouseenter',
        () => (btn.style.backgroundColor = '#f0f0f0')
      );
      btn.addEventListener(
        'mouseleave',
        () => (btn.style.backgroundColor = 'transparent')
      );
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        const currentRange = quill.getSelection() || range;

        // Insert emoji as plain text
        quill.insertText(currentRange.index, character);
        quill.setSelection(currentRange.index + character.length);

        // Don't close picker to allow multiple emoji selection
      });
      container.appendChild(btn);
    });
  }

  toggleEmojiPicker(quill: any) {
    this.isPickerOpen ? this.closeEmojiPicker() : this.showEmojiPicker(quill);
  }

  closeEmojiPicker() {
    const existing = document.querySelector('.custom-emoji-picker');
    if (existing) existing.remove();
    this.isPickerOpen = false;
  }

  private getFallbackEmojis(slug: string): { character: string }[] {
    const fallbacks: { [key: string]: string[] } = {
      'smileys-emotion': [
        '😀',
        '😃',
        '😄',
        '😁',
        '😆',
        '😅',
        '😂',
        '🤣',
        '😊',
        '😇',
        '🙂',
        '🙃',
        '😉',
        '😌',
        '😍',
        '🥰',
        '😘',
        '😗',
        '😙',
        '😚',
        '😋',
        '😛',
        '😝',
        '😜',
        '🤪',
        '🤨',
        '🧐',
        '🤓',
        '😎',
        '🤩',
        '🥳',
        '😏',
        '😒',
        '😞',
        '😔',
        '😟',
        '😕',
        '🙁',
        '☹️',
        '😣',
        '😖',
        '😫',
        '😩',
        '🥺',
        '😢',
        '😭',
        '😤',
        '😠',
        '😡',
        '🤬',
        '🤯',
        '😳',
        '🥵',
        '🥶',
        '😱',
        '😨',
        '😰',
        '😥',
        '😓',
        '🤗',
      ],
      'people-body': [
        '👍',
        '👎',
        '👌',
        '✌️',
        '🤞',
        '🤟',
        '🤘',
        '🤙',
        '👈',
        '👉',
        '👆',
        '👇',
        '☝️',
        '👋',
        '🤚',
        '🖐',
        '✋',
        '🖖',
        '👏',
        '🙌',
        '👐',
        '🤲',
        '🤝',
        '🙏',
        '✍️',
        '💅',
        '🤳',
        '💪',
        '🦾',
        '🦿',
        '🦵',
        '🦶',
        '👂',
        '🦻',
        '👃',
        '🧠',
        '🫀',
        '🫁',
        '🦷',
        '🦴',
        '👀',
        '👁️',
        '👅',
        '👄',
      ],
      'animals-nature': [
        '🐶',
        '🐱',
        '🐭',
        '🐹',
        '🐰',
        '🦊',
        '🐻',
        '🐼',
        '🐨',
        '🐯',
        '🦁',
        '🐮',
        '🐷',
        '🐽',
        '🐸',
        '🐵',
        '🙈',
        '🙉',
        '🙊',
        '🐒',
        '🐔',
        '🐧',
        '🐦',
        '🐤',
        '🐣',
        '🐥',
        '🦆',
        '🦅',
        '🦉',
        '🦇',
        '🐺',
        '🐗',
        '🐴',
        '🦄',
        '🐝',
        '🪱',
        '🐛',
        '🦋',
        '🐌',
        '🐞',
        '🐜',
        '🪰',
        '🪲',
        '🪳',
        '🦟',
        '🦗',
        '🕷️',
        '🕸️',
        '🦂',
        '🐢',
      ],
      'food-drink': [
        '🍎',
        '🍐',
        '🍊',
        '🍋',
        '🍌',
        '🍉',
        '🍇',
        '🍓',
        '🫐',
        '🍈',
        '🍒',
        '🍑',
        '🥭',
        '🍍',
        '🥥',
        '🥝',
        '🍅',
        '🍆',
        '🥑',
        '🥦',
        '🥬',
        '🥒',
        '🌶️',
        '🫑',
        '🌽',
        '🥕',
        '🫒',
        '🧄',
        '🧅',
        '🥔',
        '🍠',
        '🥐',
        '🥯',
        '🍞',
        '🥖',
        '🥨',
        '🧀',
        '🥚',
        '🍳',
        '🧈',
        '🥞',
        '🧇',
        '🥓',
        '🥩',
        '🍗',
        '🍖',
        '🦴',
        '🌭',
        '🍔',
        '🍟',
      ],
      'travel-places': [
        '🚗',
        '🚕',
        '🚙',
        '🚌',
        '🚎',
        '🏎️',
        '🚓',
        '🚑',
        '🚒',
        '🚐',
        '🛻',
        '🚚',
        '🚛',
        '🚜',
        '🏍️',
        '🛵',
        '🚲',
        '🛴',
        '🛹',
        '🛼',
        '🚁',
        '🛸',
        '✈️',
        '🛩️',
        '🪂',
        '💺',
        '🚀',
        '🛰️',
        '🚢',
        '⛵',
        '🚤',
        '🛥️',
        '🛳️',
        '⛴️',
        '🚧',
        '⛽',
        '🚏',
        '🚥',
        '🚦',
        '🚨',
        '🚭',
        '🏠',
        '🏡',
        '🏘️',
        '🏚️',
        '🏗️',
        '🏭',
        '🏢',
        '🏬',
        '🏣',
        '🏤',
      ],
      activities: [
        '⚽',
        '🏀',
        '🏈',
        '⚾',
        '🥎',
        '🎾',
        '🏐',
        '🏉',
        '🥏',
        '🎱',
        '🪀',
        '🏓',
        '🏸',
        '🏒',
        '🏑',
        '🥍',
        '🏏',
        '🪃',
        '🥅',
        '⛳',
        '🪁',
        '🏹',
        '🎣',
        '🤿',
        '🥊',
        '🥋',
        '🎽',
        '🛹',
        '🛷',
        '⛸️',
        '🥌',
        '🎿',
        '⛷️',
        '🏂',
        '🪂',
        '🏋️',
        '🤼',
        '🤸',
        '⛹️',
        '🤺',
        '🏊',
        '🏄',
        '🏇',
        '🧘',
        '🏃',
        '🚶',
        '🧗',
        '🤾',
        '🏌️',
        '🏋️',
      ],
      objects: [
        '⌚',
        '📱',
        '📲',
        '💻',
        '⌨️',
        '🖥️',
        '🖨️',
        '🖱️',
        '🖲️',
        '🕹️',
        '🗜️',
        '💽',
        '💾',
        '💿',
        '📀',
        '📼',
        '📷',
        '📸',
        '📹',
        '🎥',
        '📽️',
        '🎞️',
        '📞',
        '☎️',
        '📟',
        '📠',
        '📺',
        '📻',
        '🎙️',
        '🎚️',
        '🎛️',
        '🧭',
        '⏱️',
        '⏲️',
        '⏰',
        '🕰️',
        '⌛',
        '⏳',
        '📡',
        '🔋',
        '🔌',
        '💡',
        '🔦',
        '🕯️',
        '🪔',
        '🧯',
        '🛢️',
        '💸',
        '💵',
        '💴',
        '💶',
      ],
      symbols: [
        '❤️',
        '🧡',
        '💛',
        '💚',
        '💙',
        '💜',
        '🖤',
        '🤍',
        '🤎',
        '💔',
        '❣️',
        '💕',
        '💖',
        '💗',
        '💘',
        '💝',
        '💞',
        '💟',
        '☮️',
        '✝️',
        '☪️',
        '🕉️',
        '☸️',
        '✡️',
        '🔯',
        '🕎',
        '☯️',
        '☦️',
        '🛐',
        '⛎',
        '♈',
        '♉',
        '♊',
        '♋',
        '♌',
        '♍',
        '♎',
        '♏',
        '♐',
        '♑',
        '♒',
        '♓',
        '🆔',
        '⚛️',
        '🉑',
        '☢️',
        '☣️',
        '📴',
        '📳',
        '🈶',
        '🈚',
      ],
      flags: [
        '🏁',
        '🚩',
        '🎌',
        '🏴',
        '🏳️',
        '🏳️‍🌈',
        '🏳️‍⚧️',
        '🏴‍☠️',
        '🇦🇩',
        '🇦🇪',
        '🇦🇫',
        '🇦🇬',
        '🇦🇮',
        '🇦🇱',
        '🇦🇲',
        '🇦🇴',
        '🇦🇶',
        '🇦🇷',
        '🇦🇸',
        '🇦🇹',
        '🇦🇺',
        '🇦🇼',
        '🇦🇽',
        '🇦🇿',
        '🇧🇦',
        '🇧🇧',
        '🇧🇩',
        '🇧🇪',
        '🇧🇫',
        '🇧🇬',
        '🇧🇭',
        '🇧🇮',
        '🇧🇯',
        '🇧🇱',
        '🇧🇲',
        '🇧🇳',
        '🇧🇴',
        '🇧🇶',
        '🇧🇷',
        '🇧🇸',
        '🇧🇹',
        '🇧🇻',
        '🇧🇼',
        '🇧🇾',
        '🇧🇿',
        '🇨🇦',
        '🇨🇨',
        '🇨🇩',
        '🇨🇫',
        '🇨🇬',
        '🇨🇭',
        '🇨🇮',
        '🇨🇰',
        '🇨🇱',
        '🇨🇲',
        '🇨🇳',
        '🇨🇴',
        '🇨🇵',
        '🇨🇷',
        '🇨🇺',
        '🇨🇻',
        '🇨🇼',
        '🇨🇽',
        '🇨🇾',
        '🇨🇿',
        '🇩🇪',
        '🇩🇬',
        '🇩🇯',
        '🇩🇰',
        '🇩🇲',
        '🇩🇴',
        '🇩🇿',
        '🇪🇦',
        '🇪🇨',
        '🇪🇪',
        '🇪🇬',
        '🇪🇭',
        '🇪🇷',
        '🇪🇸',
        '🇪🇹',
        '🇪🇺',
        '🇫🇮',
        '🇫🇯',
        '🇫🇰',
        '🇫🇲',
        '🇫🇴',
        '🇫🇷',
        '🇬🇦',
        '🇬🇧',
        '🇬🇩',
        '🇬🇪',
        '🇬🇫',
        '🇬🇬',
        '🇬🇭',
        '🇬🇮',
        '🇬🇱',
        '🇬🇲',
        '🇬🇳',
        '🇬🇵',
        '🇬🇶',
        '🇬🇷',
        '🇬🇸',
        '🇬🇹',
        '🇬🇺',
        '🇬🇼',
        '🇬🇾',
        '🇭🇰',
        '🇭🇲',
        '🇭🇳',
        '🇭🇷',
        '🇭🇹',
        '🇭🇺',
        '🇮🇨',
        '🇮🇩',
        '🇮🇪',
        '🇮🇱',
        '🇮🇲',
        '🇮🇳',
        '🇮🇴',
        '🇮🇶',
        '🇮🇷',
        '🇮🇸',
        '🇮🇹',
        '🇯🇪',
        '🇯🇲',
        '🇯🇴',
        '🇯🇵',
        '🇰🇪',
        '🇰🇬',
        '🇰🇭',
        '🇰🇮',
        '🇰🇲',
        '🇰🇳',
        '🇰🇵',
        '🇰🇷',
        '🇰🇼',
        '🇰🇾',
        '🇰🇿',
        '🇱🇦',
        '🇱🇧',
        '🇱🇨',
        '🇱🇮',
        '🇱🇰',
        '🇱🇷',
        '🇱🇸',
        '🇱🇹',
        '🇱🇺',
        '🇱🇻',
        '🇱🇾',
        '🇲🇦',
        '🇲🇨',
        '🇲🇩',
        '🇲🇪',
        '🇲🇫',
        '🇲🇬',
        '🇲🇭',
        '🇲🇰',
        '🇲🇱',
        '🇲🇲',
        '🇲🇳',
        '🇲🇴',
        '🇲🇵',
        '🇲🇶',
        '🇲🇷',
        '🇲🇸',
        '🇲🇹',
        '🇲🇺',
        '🇲🇻',
        '🇲🇼',
        '🇲🇽',
        '🇲🇾',
        '🇲🇿',
        '🇳🇦',
        '🇳🇨',
        '🇳🇪',
        '🇳🇫',
        '🇳🇬',
        '🇳🇮',
        '🇳🇱',
        '🇳🇴',
        '🇳🇵',
        '🇳🇷',
        '🇳🇺',
        '🇳🇿',
        '🇴🇲',
        '🇵🇦',
        '🇵🇪',
        '🇵🇫',
        '🇵🇬',
        '🇵🇭',
        '🇵🇰',
        '🇵🇱',
        '🇵🇲',
        '🇵🇳',
        '🇵🇷',
        '🇵🇸',
        '🇵🇹',
        '🇵🇼',
        '🇵🇾',
        '🇶🇦',
        '🇷🇪',
        '🇷🇴',
        '🇷🇸',
        '🇷🇺',
        '🇷🇼',
        '🇸🇦',
        '🇸🇧',
        '🇸🇨',
        '🇸🇩',
        '🇸🇪',
        '🇸🇬',
        '🇸🇭',
        '🇸🇮',
        '🇸🇯',
        '🇸🇰',
        '🇸🇱',
        '🇸🇲',
        '🇸🇳',
        '🇸🇴',
        '🇸🇷',
        '🇸🇸',
        '🇸🇹',
        '🇸🇻',
        '🇸🇽',
        '🇸🇾',
        '🇸🇿',
        '🇹🇦',
        '🇹🇨',
        '🇹🇩',
        '🇹🇫',
        '🇹🇬',
        '🇹🇭',
        '🇹🇯',
        '🇹🇰',
        '🇹🇱',
        '🇹🇲',
        '🇹🇳',
        '🇹🇴',
        '🇹🇷',
        '🇹🇹',
        '🇹🇻',
        '🇹🇼',
        '🇹🇿',
        '🇺🇦',
        '🇺🇬',
        '🇺🇲',
        '🇺🇳',
        '🇺🇸',
        '🇺🇾',
        '🇺🇿',
        '🇻🇦',
        '🇻🇨',
        '🇻🇪',
        '🇻🇬',
        '🇻🇮',
        '🇻🇳',
        '🇻🇺',
        '🇼🇫',
        '🇼🇸',
        '🇽🇰',
        '🇾🇪',
        '🇾🇹',
        '🇿🇦',
        '🇿🇲',
        '🇿🇼',
      ],
    };

    const emojis = fallbacks[slug] || [];
    return emojis.map((character) => ({ character }));
  }
}

// Register modules
Quill.register('modules/emoji', EmojiModule);
Quill.register('modules/imageUploader', ImageUploader);

// Register custom link format that opens in new window
const Link = Quill.import('formats/link') as any;

class CustomLink extends Link {
  static create(value: any) {
    const node = super.create(value);
    node.setAttribute('href', value);
    node.setAttribute('target', '_blank');
    node.setAttribute('rel', 'noopener noreferrer');
    return node;
  }
}

Quill.register('formats/link', CustomLink, true);

@Injectable({
  providedIn: 'root',
})
export class QuillConfigService {
  constructor(
    private imageUploadService: ImageUploadService,
    private emojiApi: EmojiApiService
  ) {}

  getDefaultModules() {
    const self = this;
    return {
      toolbar: {
        container: [
          ['bold', 'italic', 'underline', 'strike'],
          [{ header: 1 }, { header: 2 }],
          [{ list: 'ordered' }, { list: 'bullet' }],
          [{ indent: '-1' }, { indent: '+1' }],
          [{ header: [1, 2, 3, 4, 5, 6, false] }],
          ['clean'],
          ['link', 'image', 'video'],
          ['emoji'],
        ],
        handlers: {
          emoji: function () {
            // Handler will be automatically set by the emoji module
          },
        },
      },
      imageUploader: {
        upload: (file: File): Promise<string> => {
          return new Promise((resolve, reject) => {
            self.imageUploadService.uploadImage(file).subscribe({
              next: (response) => {
                resolve(response.image_file);
              },
              error: (error) => {
                console.error('Image upload failed:', error);
                reject(error);
              },
            });
          });
        },
      },
      emoji: {
        fetchCategories: () => self.emojiApi.getCategories().toPromise(),
        fetchEmojisByCategory: (slug: string) =>
          self.emojiApi.getEmojisByCategory(slug).toPromise(),
      },
      // Add clipboard configuration to preserve emojis
      clipboard: {
        matchVisual: false,
      },
    };
  }
}
